import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, CheckCircle, AlertCircle, Filter, Save, 
  ChevronDown, ChevronUp, Target, Check, X
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';
import { SIX_PILLARS } from '../data/pillars';

const QuestionSelectionPage = () => {
  const [orgTypes, setOrgTypes] = useState([]);
  const [selectedOrgType, setSelectedOrgType] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [requiredQuestions, setRequiredQuestions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedPillars, setExpandedPillars] = useState({});
  const [selectionStatus, setSelectionStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const PILLARS = SIX_PILLARS.map((p) => ({ number: p.id, name: p.name }));

  useEffect(() => {
    fetchOrgTypes();
    fetchQuestions();
    fetchSelectionStatus();
  }, []);

  const fetchSelectionStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/required-questions-status`);
      if (response.ok) setSelectionStatus(await response.json());
    } catch (err) {
      console.error('Error fetching selection status:', err);
    }
  };

  const statusFor = (orgType) =>
    selectionStatus?.types?.find((t) => t.org_type === (orgType.name || '').toUpperCase());

  const fetchOrgTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/organization-types`);
      if (response.ok) {
        const data = await response.json();
        setOrgTypes(data.organization_types || []);
      }
    } catch (error) {
      console.error('Error fetching organization types:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/questions`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequiredQuestions = useCallback(async (orgTypeName) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/required-questions/${orgTypeName}`);
      if (response.ok) {
        const data = await response.json();
        setRequiredQuestions(new Set(data.required_question_ids || []));
      } else {
        setRequiredQuestions(new Set());
      }
    } catch (error) {
      console.error('Error fetching required questions:', error);
      setRequiredQuestions(new Set());
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    if (selectedOrgType) {
      fetchRequiredQuestions(selectedOrgType.name);
    }
  }, [selectedOrgType, fetchRequiredQuestions]);

  const handleOrgTypeSelect = (orgType) => {
    setSelectedOrgType(orgType);
    setError('');
    setSuccess('');
    // Expand all pillars by default
    const expanded = {};
    PILLARS.forEach(p => { expanded[p.number] = true; });
    setExpandedPillars(expanded);
  };

  const toggleRequired = (questionId) => {
    setRequiredQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const getFilteredQuestions = () => {
    if (!selectedOrgType) return [];
    return questions.filter(q => 
      q.applicable_to?.includes(selectedOrgType.name) && q.is_active
    );
  };

  const getQuestionsByPillar = (pillarNumber) => {
    return getFilteredQuestions().filter(q => q.pillar_number === pillarNumber);
  };

  const getSelectedCountByPillar = (pillarNumber) => {
    const pillarQuestions = getQuestionsByPillar(pillarNumber);
    return pillarQuestions.filter(q => requiredQuestions.has(q.question_id)).length;
  };

  const getSelectedCount = (type) => {
    const filtered = getFilteredQuestions().filter(q => q.question_type === type);
    return filtered.filter(q => requiredQuestions.has(q.question_id)).length;
  };

  const getTotalSelected = () => {
    return getFilteredQuestions().filter(q => requiredQuestions.has(q.question_id)).length;
  };

  const handleSaveSelection = async () => {
    // status refreshed at the end of this handler
    if (!selectedOrgType) return;

    const qualRequired = selectedOrgType.qualitative_questions || 0;
    const quantRequired = selectedOrgType.quantitative_questions || 0;
    const qualSelected = getSelectedCount('qualitative');
    const quantSelected = getSelectedCount('quantitative');

    if (qualSelected !== qualRequired || quantSelected !== quantRequired) {
      setError(`Please select exactly ${qualRequired} qualitative and ${quantRequired} quantitative questions (currently: ${qualSelected} qual, ${quantSelected} quant)`);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/required-questions/${selectedOrgType.name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          required_question_ids: Array.from(requiredQuestions)
        })
      });

      if (response.ok) {
        setSuccess('Question selection saved successfully!');
        fetchSelectionStatus();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save selection');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const autoSelectDistributed = () => {
    if (!selectedOrgType) return;

    const qualRequired = selectedOrgType.qualitative_questions || 0;
    const quantRequired = selectedOrgType.quantitative_questions || 0;
    
    // Calculate questions per pillar (distribute evenly)
    const qualPerPillar = Math.ceil(qualRequired / 8);
    const quantPerPillar = Math.ceil(quantRequired / 8);
    
    const newRequired = new Set();
    let qualCount = 0;
    let quantCount = 0;

    PILLARS.forEach(pillar => {
      const pillarQuestions = getQuestionsByPillar(pillar.number);
      const qualQuestions = pillarQuestions.filter(q => q.question_type === 'qualitative');
      const quantQuestions = pillarQuestions.filter(q => q.question_type === 'quantitative');

      // Select qualitative questions for this pillar
      qualQuestions.slice(0, qualPerPillar).forEach(q => {
        if (qualCount < qualRequired) {
          newRequired.add(q.question_id);
          qualCount++;
        }
      });

      // Select quantitative questions for this pillar
      quantQuestions.slice(0, quantPerPillar).forEach(q => {
        if (quantCount < quantRequired) {
          newRequired.add(q.question_id);
          quantCount++;
        }
      });
    });

    setRequiredQuestions(newRequired);
    setSuccess('Auto-selected questions distributed across pillars!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const togglePillar = (pillarNumber) => {
    setExpandedPillars(prev => ({
      ...prev,
      [pillarNumber]: !prev[pillarNumber]
    }));
  };

  return (
    <SystemAdminLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 flex items-center gap-3">
              <Target className="w-8 h-8 text-indigo-600" />
              Selection of Assessment Questions
            </h1>
            <p className="text-gray-600 mt-1">
              Select which questions are REQUIRED for each organization type. Questions will be equally distributed across 6 pillars.
            </p>
          </div>

          {/* Selection gap warning */}
          {selectionStatus && selectionStatus.missing_count > 0 && (
            <div
              data-testid="selection-gap-banner"
              className="mb-6 flex items-start gap-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5"
            >
              <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900">
                  {selectionStatus.missing_count} organisation type{selectionStatus.missing_count > 1 ? 's' : ''} have no saved selection
                </p>
                <p className="text-sm text-amber-800 mt-1">
                  {selectionStatus.missing_types.join(', ')} — assessments for these types currently fall back to
                  the full bank of {selectionStatus.bank_total} questions across all six pillars.
                </p>
              </div>
            </div>
          )}

          {/* Step 1: Select Organization Type */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              Select Organization Type
            </h2>
            
            <div className="grid grid-cols-5 gap-4">
              {orgTypes.map((orgType) => (
                <button
                  key={orgType.id}
                  onClick={() => handleOrgTypeSelect(orgType)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedOrgType?.id === orgType.id
                      ? 'border-indigo-500 bg-indigo-50 shadow-lg'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="text-center">
                    <p className="font-bold text-gray-900">{orgType.display_name || orgType.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{orgType.employee_range}</p>
                    <div className="mt-2 text-xs">
                      <span className="text-blue-600 font-semibold">{orgType.qualitative_questions} Qual</span>
                      {' + '}
                      <span className="text-green-600 font-semibold">{orgType.quantitative_questions} Quant</span>
                    </div>
                    <p className="text-indigo-600 font-bold mt-1">
                      = {(orgType.qualitative_questions || 0) + (orgType.quantitative_questions || 0)} Total
                    </p>
                    <span
                      data-testid={`selection-status-${(orgType.name || '').toUpperCase()}`}
                      className={`mt-3 inline-block px-2 py-1 rounded-full text-[11px] font-bold ${
                        statusFor(orgType)?.has_selection
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {statusFor(orgType)?.has_selection
                        ? `${statusFor(orgType).selected_count} selected`
                        : `No selection — using all ${selectionStatus?.bank_total ?? 60}`}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Select Questions */}
          {selectedOrgType && (
            <>
              {/* Selection Summary */}
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                      Select Required Questions for {selectedOrgType.display_name}
                    </h2>
                    <p className="text-gray-600 text-sm mt-1">
                      Mark questions as "Required" to include them in the assessment
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <button
                      onClick={autoSelectDistributed}
                      className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-semibold hover:bg-purple-200 transition-all"
                    >
                      🎯 Auto-Select (Distributed)
                    </button>
                    <button
                      onClick={handleSaveSelection}
                      disabled={saving}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Selection
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Progress */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className={`p-4 rounded-xl border-2 ${
                    getSelectedCount('qualitative') === selectedOrgType.qualitative_questions
                      ? 'bg-green-50 border-green-300'
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <p className="text-sm text-gray-600">Qualitative Questions</p>
                    <p className="text-2xl font-black">
                      <span className={getSelectedCount('qualitative') === selectedOrgType.qualitative_questions ? 'text-green-600' : 'text-blue-600'}>
                        {getSelectedCount('qualitative')}
                      </span>
                      <span className="text-gray-400"> / {selectedOrgType.qualitative_questions}</span>
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl border-2 ${
                    getSelectedCount('quantitative') === selectedOrgType.quantitative_questions
                      ? 'bg-green-50 border-green-300'
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <p className="text-sm text-gray-600">Quantitative Questions</p>
                    <p className="text-2xl font-black">
                      <span className={getSelectedCount('quantitative') === selectedOrgType.quantitative_questions ? 'text-green-600' : 'text-emerald-600'}>
                        {getSelectedCount('quantitative')}
                      </span>
                      <span className="text-gray-400"> / {selectedOrgType.quantitative_questions}</span>
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl border-2 ${
                    getTotalSelected() === (selectedOrgType.qualitative_questions + selectedOrgType.quantitative_questions)
                      ? 'bg-green-50 border-green-400'
                      : 'bg-indigo-50 border-indigo-200'
                  }`}>
                    <p className="text-sm text-gray-600">Total Selected</p>
                    <p className="text-2xl font-black">
                      <span className={getTotalSelected() === (selectedOrgType.qualitative_questions + selectedOrgType.quantitative_questions) ? 'text-green-600' : 'text-indigo-600'}>
                        {getTotalSelected()}
                      </span>
                      <span className="text-gray-400"> / {(selectedOrgType.qualitative_questions || 0) + (selectedOrgType.quantitative_questions || 0)}</span>
                    </p>
                    {getTotalSelected() === (selectedOrgType.qualitative_questions + selectedOrgType.quantitative_questions) && (
                      <div className="flex items-center gap-1 text-green-600 text-sm mt-1">
                        <CheckCircle className="w-4 h-4" />
                        Complete!
                      </div>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                  </div>
                )}
                {success && (
                  <div className="mt-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                    {success}
                  </div>
                )}
              </div>

              {/* Questions by Pillar */}
              <div className="space-y-4">
                {PILLARS.map((pillar) => {
                  const pillarQuestions = getQuestionsByPillar(pillar.number);
                  const qualQuestions = pillarQuestions.filter(q => q.question_type === 'qualitative');
                  const quantQuestions = pillarQuestions.filter(q => q.question_type === 'quantitative');
                  const selectedCount = getSelectedCountByPillar(pillar.number);
                  
                  if (pillarQuestions.length === 0) return null;

                  return (
                    <div key={pillar.number} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                      <button
                        onClick={() => togglePillar(pillar.number)}
                        className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-150 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <span className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                            {pillar.number}
                          </span>
                          <div className="text-left">
                            <h3 className="font-bold text-gray-900">{pillar.name}</h3>
                            <p className="text-sm text-gray-500">
                              {qualQuestions.length} qual + {quantQuestions.length} quant = {pillarQuestions.length} applicable
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                            selectedCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {selectedCount} selected
                          </span>
                          {expandedPillars[pillar.number] ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {expandedPillars[pillar.number] && (
                        <div className="p-4 border-t border-gray-200">
                          {/* Qualitative Questions */}
                          {qualQuestions.length > 0 && (
                            <div className="mb-4">
                              <h4 className="text-sm font-bold text-blue-700 mb-2 flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                Qualitative Questions ({qualQuestions.length})
                              </h4>
                              <div className="space-y-2">
                                {qualQuestions.map((question) => (
                                  <div
                                    key={question.question_id}
                                    onClick={() => toggleRequired(question.question_id)}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      requiredQuestions.has(question.question_id)
                                        ? 'border-green-400 bg-green-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                        requiredQuestions.has(question.question_id)
                                          ? 'border-green-500 bg-green-500 text-white'
                                          : 'border-gray-300'
                                      }`}>
                                        {requiredQuestions.has(question.question_id) && <Check className="w-4 h-4" />}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                                            {question.question_id}
                                          </span>
                                          {requiredQuestions.has(question.question_id) && (
                                            <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">
                                              REQUIRED
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-gray-900 text-sm">{question.question_text}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Quantitative Questions */}
                          {quantQuestions.length > 0 && (
                            <div>
                              <h4 className="text-sm font-bold text-green-700 mb-2 flex items-center gap-2">
                                <Filter className="w-4 h-4" />
                                Quantitative Questions ({quantQuestions.length})
                              </h4>
                              <div className="space-y-2">
                                {quantQuestions.map((question) => (
                                  <div
                                    key={question.question_id}
                                    onClick={() => toggleRequired(question.question_id)}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      requiredQuestions.has(question.question_id)
                                        ? 'border-green-400 bg-green-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                        requiredQuestions.has(question.question_id)
                                          ? 'border-green-500 bg-green-500 text-white'
                                          : 'border-gray-300'
                                      }`}>
                                        {requiredQuestions.has(question.question_id) && <Check className="w-4 h-4" />}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">
                                            {question.question_id}
                                          </span>
                                          {requiredQuestions.has(question.question_id) && (
                                            <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">
                                              REQUIRED
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-gray-900 text-sm">{question.question_text}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* No Org Type Selected */}
          {!selectedOrgType && !loading && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">Select an Organization Type</h3>
              <p className="text-gray-500">Choose an organization type above to see and select required questions</p>
            </div>
          )}
        </div>
      </div>
    </SystemAdminLayout>
  );
};

export default QuestionSelectionPage;
